// React hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Helpers i API servisi
import { transformRoute } from "helpers";
import {
  getCronJobAPI,
  resetCronJobStatusAPI,
  searchCronJobExecutionsAPI,
} from "services/api/cronjobs/api";
import {
  CronJob,
  CronJobExecutionEventType,
  CronJobExecutionRow,
  CronJobExecutionSearchFormValues,
} from "services/api/cronjobs/types";
import {
  initialCronJobExecutionPagination,
  initialCronJobExecutionSearchForm,
} from "services/api/cronjobs/consts";
import { DataWithPagination } from "services/api";
import { Pagination } from "services/search";

// Tipovi i konstante
import { CronJobDetailPageCmp, CronJobDetailPageProps } from "./types";
import { CRON_JOB_EXECUTION_COLUMNS } from "./consts";
import {
  CRON_JOB_DETAIL_PAGE_TITLE,
  CRON_JOB_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Loader } from "ui/components/Loader";
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { NoResults } from "ui/components/NoResults";
import { TableSearch } from "ui/components/Table/TableSearch";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import CronJobInfoCard from "./components/CronJobInfoCard/CronJobInfoCard";
import CronJobExecutionsFilters from "./components/CronJobExecutionsFilters/CronJobExecutionsFilters";

const emptyExecutions: DataWithPagination<CronJobExecutionRow> = {
  data: [],
  pagination: initialCronJobExecutionPagination,
};

/**
 * Jedna vrsta obrade i popis njezinih obrada. Klik na obradu otvara njezin log.
 */
const CronJobDetailPage: CronJobDetailPageCmp = (_props: CronJobDetailPageProps) => {
  const { cronJobId } = useParams<{ cronJobId: string }>();
  const navigate = useNavigate();

  const [cronJob, setCronJob] = useState<CronJob>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [executions, setExecutions] =
    useState<DataWithPagination<CronJobExecutionRow>>(emptyExecutions);
  const [isLoadingExecutions, setIsLoadingExecutions] = useState<boolean>(false);
  const [filterValues, setFilterValues] = useState<CronJobExecutionSearchFormValues>(
    initialCronJobExecutionSearchForm
  );
  const [showResetDialog, setShowResetDialog] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);

  const fetchCronJob = useCallback(async () => {
    if (!cronJobId) return;
    setIsLoading(true);
    try {
      setCronJob(await getCronJobAPI(cronJobId));
    } catch {
      // greška je prikazana kroz snackbar
    } finally {
      setIsLoading(false);
    }
  }, [cronJobId]);

  const fetchExecutions = useCallback(
    async (pagination: Pagination, filters: CronJobExecutionSearchFormValues) => {
      if (!cronJobId) return;
      setIsLoadingExecutions(true);
      try {
        setExecutions(await searchCronJobExecutionsAPI(cronJobId, pagination, filters));
      } catch {
        setExecutions(emptyExecutions);
      } finally {
        setIsLoadingExecutions(false);
      }
    },
    [cronJobId]
  );

  useEffect(() => {
    fetchCronJob();
    fetchExecutions(initialCronJobExecutionPagination, initialCronJobExecutionSearchForm);
  }, [fetchCronJob, fetchExecutions]);

  const handleRefresh = useCallback(() => {
    fetchCronJob();
    fetchExecutions(executions.pagination, filterValues);
  }, [fetchCronJob, fetchExecutions, executions.pagination, filterValues]);

  const handleSearchChange = useCallback(
    (values: CronJobExecutionSearchFormValues) => {
      setFilterValues(values);
      fetchExecutions({ ...executions.pagination, pageNumber: 1 }, values);
    },
    [fetchExecutions, executions.pagination]
  );

  const handlePaginationChange = useCallback(
    (pagination: Pagination) => {
      fetchExecutions(pagination, filterValues);
    },
    [fetchExecutions, filterValues]
  );

  const navigateToExecution = useCallback(
    (executionId: string) => {
      if (!cronJobId) return;
      navigate(transformRoute(Routes.CronJobExecutionPageRouter, { cronJobId, executionId }));
    },
    [navigate, cronJobId]
  );

  const handleEventClick = useCallback(
    (id: string, name: string) => {
      if (name === CronJobExecutionEventType.LOGS) navigateToExecution(id);
    },
    [navigateToExecution]
  );

  const handleResetConfirm = useCallback(async () => {
    if (!cronJob) return;
    setIsResetting(true);
    try {
      setCronJob(await resetCronJobStatusAPI(cronJob.id));
      // Reset zatvara otvorenu obradu kao 'aborted' — popis to mora pokazati.
      fetchExecutions(executions.pagination, filterValues);
    } catch {
      // greška je prikazana kroz snackbar
    } finally {
      setIsResetting(false);
      setShowResetDialog(false);
    }
  }, [cronJob, fetchExecutions, executions.pagination, filterValues]);

  const handleResetCancel = useCallback(() => {
    if (!isResetting) setShowResetDialog(false);
  }, [isResetting]);

  const title = CRON_JOB_DETAIL_PAGE_TITLE;
  const breadcrumbs: BreadcrumbItem[] = [
    { text: CRON_JOB_SEARCH_PAGE_TITLE.text, route: CRON_JOB_SEARCH_PAGE_TITLE.route },
  ];

  if (!cronJobId) return <></>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border-b border-slate-200">
          <PageHeader>
            <div className="flex justify-between">
              <div>
                <PageTitle
                  title={cronJob ? `${title.text} [${cronJob.name}]` : title.text}
                  icon={title.icon}
                  iconColor={title.iconColor}
                />
                <Breadcrumb items={breadcrumbs} />
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  variant="outlined"
                  startIcon="/media/icons/refresh.svg"
                  onClick={handleRefresh}
                  disabled={isLoading || isLoadingExecutions}
                >
                  Osvježi
                </Button>
                <Button
                  variant="warning"
                  startIcon="/media/icons/warning.svg"
                  onClick={() => setShowResetDialog(true)}
                  disabled={!cronJob?.canResetStatus || isResetting}
                >
                  Resetiraj status
                </Button>
              </div>
            </div>
          </PageHeader>
        </div>

        <div className="px-4 py-6 flex flex-col gap-6">
          {isLoading || !cronJob ? (
            <Loader isLoading={true}> </Loader>
          ) : (
            <CronJobInfoCard cronJob={cronJob} />
          )}

          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <p className="text-base font-bold text-slate-600">Obrade</p>
              <CronJobExecutionsFilters
                formValues={filterValues}
                onSearchChange={handleSearchChange}
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-sm">
              {!isLoadingExecutions && executions.pagination.totalPages === 0 ? (
                <NoResults
                  message={
                    filterValues.status
                      ? "Nema obrada s odabranim statusom"
                      : "Ova vrsta obrade još nije pokrenuta"
                  }
                />
              ) : (
                <TableSearch
                  columns={CRON_JOB_EXECUTION_COLUMNS}
                  data={executions.data}
                  selectableRows={false}
                  pagination={executions.pagination}
                  onPaginationChange={handlePaginationChange}
                  isLoading={isLoadingExecutions}
                  onRowClick={(id) => navigateToExecution(String(id))}
                  onEventClick={handleEventClick}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={showResetDialog}
        title="Resetiraj status vrste obrade"
        message={
          cronJob
            ? `Vrsta obrade "${cronJob.name}" vraća se iz statusa '${cronJob.status}' u 'finished', pa je planer ponovno smije pokrenuti. Otvorena obrada bit će označena kao prekinuta. Koristite samo ako je obrada zapela.`
            : ""
        }
        confirmText={isResetting ? "Resetiram…" : "Resetiraj"}
        cancelText="Odustani"
        onConfirm={handleResetConfirm}
        onCancel={handleResetCancel}
      />
    </>
  );
};

CronJobDetailPage.route = Routes.CronJobDetailPageRouter;

export default CronJobDetailPage;
